import { useState, useContext } from "react";
import {
  Card,
  Grid,
  Icon,
  VStack,
  useColorModeValue as mode,
  Button,
  Tooltip,
  Flex,
  Text,
} from "@chakra-ui/react";
import { Reorder } from "framer-motion";
import { LuPlus, LuTrash, LuEye, LuEyeOff } from "react-icons/lu";
import toast from "react-hot-toast";

// Components
import Preview from "./preview";

// State
import { createBlankCarousel } from "./utils/app-reducer";

// Types
import type { Carousel, Settings, Theme } from "types/carousel";

// Utils
import { getToken } from "../../firebaseClient";
import { useSession } from "next-auth/react";
import { MainContext } from "context/mainContext";
import { getCreditBack, useCredit } from "controllers/subscription";
import { getAccount } from "utils/sessionHelper";

const baseUrl = "https://us-central1-ez4cast.cloudfunctions.net";

const generateAISingle = async (carousels, session, useBestAi) => {
  const token = await getToken(session, "aiCarouselGenerator");
  const userDescription = getAccount(session)?.description;
  const history = carousels.filter((carousel) => carousel.type === "introduction" || carousel.type === "content");
  return await fetch(`${baseUrl}/linkedinCarousell-generateaiSingle`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      tokenuserid: session.user.uid,
    },
    body: JSON.stringify({
      input: JSON.stringify(history),
      description: userDescription,
      type: "new-slide",
      useBestAi,
      isInfluentLeader: true,
    }),
  });
}

export default function Thumbnails({
  carousels,
  selectedCarouselId,
  setselectedCarouselId,
  addCarousel,
  reorderCarousels,
  removeCarousel,
  updateCarouselVisibility,
  theme,
  settings,
}: {
  carousels: Carousel[];
  selectedCarouselId: string;
  setselectedCarouselId: (id: string) => void;
  addCarousel: (carousel: Carousel) => void;
  reorderCarousels: (carousels: Carousel[]) => void;
  removeCarousel: (id: string) => void;
  updateCarouselVisibility: (id: string, visible: boolean) => void;
  theme: Theme;
  settings: Settings;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectOptionOpen, setIsSelectOptionOpen] = useState(false);
  const { data: session } = useSession() ?? {};
  const mainContext = useContext(MainContext);

  const handleSlideGeneration = async () => {
    setIsSelectOptionOpen(false);
    setIsLoading(true);
    let useBestAi = await useCredit(
      session,
      mainContext,
      "creditsBestai",
      false
    );
    try {
      const res = await generateAISingle(
        carousels,
        session,
        useBestAi,
      );
      let data = await res.json();
      if (data?.success === 1 && data?.carousel) {
        addCarousel(data.carousel);
      } else if (data.success === 0 && data.error) {
        toast.error(data.error);
      } else {
        throw new Error();
      }
    } catch (error) {
      toast.error(
        "Something went wrong while generating the carousel. Please try again."
      );
      getCreditBack(session, "creditsBestai");
    }
    setIsLoading(false);
  };
  return (
    <Grid
      as={Reorder.Group}
      onReorder={reorderCarousels}
      values={carousels}
      axis="x"
      gap={4}
      gridAutoFlow={"column"}
      gridAutoColumns={"86px"}
    >
      <VStack position={"relative"}>
        <Preview
          content={carousels[0]}
          thumbnail={{
            setselectedCarouselId,
            selectedCarouselId,
          }}
          theme={theme}
          settings={settings}
          scale={0.19}
        />
        {
          carousels[0].invisible && (
            <Flex
              as={"button"}
              position={"absolute"}
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg={"gray.100"}
              opacity={0.8}
              align="center"
              justify="center"
              onClick={() => {
                setselectedCarouselId(carousels[0].id);
              }}
            ><Text fontSize="xs" fontWeight="bold" >Hidden</Text></Flex>
          )
        }
        <Tooltip
          label={carousels[0].invisible ? "Enable Slide" : "Hide Slide"}
          placement={"right"}
          hasArrow
          size="xs"
        >
          <Button
            onClick={() => {
              updateCarouselVisibility(
                carousels[0].id,
                !carousels[0].invisible
              );
            }}
            position={"absolute"}
            right={0}
            top={0}
            size="xs"
            variant="action"
            color={"gray.700"}
          >
            <Icon as={carousels[0].invisible ? LuEye : LuEyeOff} w={3} h={3} />
          </Button>
        </Tooltip>
      </VStack>
      {carousels.map((carousel, idx) => {
        if (
          idx === 0 ||
          idx === carousels.length - 1 ||
          idx === carousels.length - 2
        ) {
          return;
        }
        return (
          <VStack
            as={Reorder.Item}
            value={carousel}
            key={carousel.id}
            position={"relative"}
          >
            <Preview
              content={carousel}
              thumbnail={{
                setselectedCarouselId,
                selectedCarouselId,
              }}
              theme={theme}
              settings={settings}
              slideNo={idx}
              scale={0.19}
            />
            <Button
              onClick={() => {
                setselectedCarouselId(carousels[idx - 1].id);
                removeCarousel(carousel.id);
              }}
              position={"absolute"}
              right={0}
              top={0}
              size="xs"
              variant="actionDestructive"
              color={"gray.700"}
            >
              <Icon as={LuTrash} w={3} h={3} />
            </Button>
          </VStack>
        );
      })}
      <Card
        aspectRatio={4 / 5}
        opacity={mode(0.3, 0.6)}
        borderWidth={"2px"}
        borderStyle={"dashed"}
        borderColor={mode("border.lightMode.active", "border.darkMode.light")}
        _hover={{
          opacity: 1,
        }}
        transition={"opacity 0.2s ease-in-out"}
        bg="transparent"
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {isSelectOptionOpen ?
          <VStack>
            <Button variant="secondary" w="full" onClick={() => { addCarousel(createBlankCarousel()); setIsSelectOptionOpen(false) }}>Blank</Button>
            <Button variant="secondaryAI" w="full" onClick={handleSlideGeneration}>⚡️ AI</Button>
          </VStack>
          :
          <Button onClick={() => setIsSelectOptionOpen(true)} w="full" h="full" variant="transparent" isLoading={isLoading}>
            <Icon
              as={LuPlus}
              color={mode("border.lightMode.active", "border.darkMode.light")}
              w={6}
              h={6}
            /></Button>}
      </Card>
      <VStack position={"relative"}>
        <Preview
          content={carousels[carousels.length - 2]}
          thumbnail={{
            setselectedCarouselId,
            selectedCarouselId,
          }}
          theme={theme}
          settings={settings}
          scale={0.19}
        />
        {
          carousels[carousels.length - 2].invisible && (
            <Flex
              as={"button"}
              position={"absolute"}
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg={"gray.100"}
              opacity={0.8}
              align="center"
              justify="center"
              onClick={() => {
                setselectedCarouselId(carousels[carousels.length - 2].id);
              }}
            ><Text fontSize="xs" fontWeight="bold" >Hidden</Text></Flex>
          )
        }
        <Tooltip
          label={
            carousels[carousels.length - 2].invisible
              ? "Enable Slide"
              : "Hide Slide"
          }
          placement={"right"}
          hasArrow
          size="xs"
        >
          <Button
            onClick={() => {
              updateCarouselVisibility(
                carousels[carousels.length - 2].id,
                !carousels[carousels.length - 2].invisible
              );
            }}
            position={"absolute"}
            right={0}
            top={0}
            size="xs"
            variant="action"
            color={"gray.700"}
          >
            <Icon
              as={carousels[carousels.length - 2].invisible ? LuEye : LuEyeOff}
              w={3}
              h={3}
            />
          </Button>
        </Tooltip>
      </VStack>
      <VStack position={"relative"}>
        <Preview
          content={carousels[carousels.length - 1]}
          thumbnail={{
            setselectedCarouselId,
            selectedCarouselId,
          }}
          theme={theme}
          settings={settings}
          scale={0.19}
        />
        {
          carousels[carousels.length - 1].invisible && (
            <Flex
              as={"button"}
              position={"absolute"}
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg={"gray.100"}
              opacity={0.8}
              align="center"
              justify="center"
              onClick={() => {
                setselectedCarouselId(carousels[carousels.length - 1].id);
              }}
            ><Text fontSize="xs" fontWeight="bold" >Hidden</Text></Flex>
          )
        }
        <Tooltip
          label={
            carousels[carousels.length - 1].invisible
              ? "Enable Slide"
              : "Hide Slide"
          }
          placement={"right"}
          hasArrow
          size="xs"
        >
          <Button
            onClick={() => {
              updateCarouselVisibility(
                carousels[carousels.length - 1].id,
                !carousels[carousels.length - 1].invisible
              );
            }}
            position={"absolute"}
            right={0}
            top={0}
            size="xs"
            variant="action"
            color={"gray.700"}
          >
            <Icon
              as={carousels[carousels.length - 1].invisible ? LuEye : LuEyeOff}
              w={3}
              h={3}
            />
          </Button>
        </Tooltip>
      </VStack>
    </Grid>
  );
}
